import { forwardRef } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import { Anchor, Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import BaseCard from "@/components/BaseCard";
import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";
import UniversalEmbedCardFooter from "@/browse/UniversalEmbedCardFooter";

import useIsImraUser from "@/imra/useIsImraUser";
import { useContentCard } from "../hooks/useContentCard";
import { SMART_BUNDLES_LABEL } from "../ContentCardV2.constants";

import {
  BundleCardMetadata,
  CardViewmodelAndProps,
} from "../ContentCardV2.interface";

import {
  cardActionsContainerCss,
  contentSettingsModeCss,
  linkCss,
} from "./styles";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

export const BundleCard = forwardRef<HTMLElement, CardViewmodelAndProps>(
  (props, ref) => {
    const router = useRouter();
    const {
      id: contentId,
      contentSettingsGroups,
      dataQaSelector = "",
      display_date: displayDate,
      images = [],
      isContentSettingsEnabled,
      isUniversalEmbed = false,
      metadata,
      title,
      url,
      visibility_markers: visibilityMarkers,
    } = props;

    const {
      content_type: contentType,
      label = "",
      asset_count: assetCount,
    } = metadata as BundleCardMetadata;

    const isImraUser = useIsImraUser();

    const { assignHref, badges, showAssignmentAnchor } = useContentCard({
      contentId,
      contentType,
      label,
      visibilityMarkers,
    });

    return (
      <BaseCard
        ref={ref}
        badges={badges}
        contentId={contentId}
        // The database shows that only Smart Bundles have no label (but we have
        // problematic Bundles with no "data"), and the Product team decided to
        // use a different designation for such cards.
        // NOTE: we could be more defensive and also check if the bundle has no
        // `assetCount`; Smart Bundles also don't have counts.
        contentTag={label || SMART_BUNDLES_LABEL}
        dataQaSelector={dataQaSelector}
        displayDate={isImraUser ? "" : displayDate}
        images={images}
        title={title}
        url={url}
        assetCount={assetCount}
        specialContent={
          <>
            {isContentSettingsEnabled && (
              <AdminContentSettingsCardFooter
                contentId={contentId}
                contentSettingsGroups={contentSettingsGroups || []}
                visibilityMarkers={visibilityMarkers}
                style={contentSettingsModeCss}
              />
            )}

            {isUniversalEmbed && (
              <UniversalEmbedCardFooter
                contentId={contentId}
                title={title}
                contentType="text-set"
                requestId={(router.query["request_id"] as string) || ""}
                productFamily={(router.query["product_family"] as string) || ""}
                addOnToken={(router.query["addOnToken"] as string) || ""}
                courseId={(router.query["courseId"] as string) || ""}
                itemId={
                  (router.query["itemId"] as string) ||
                  (router.query["postId"] as string) ||
                  ""
                }
                itemType={
                  (router.query["itemType"] as string) ||
                  (router.query["postId"] ? "courseWork" : undefined)
                }
              />
            )}

            {!isUniversalEmbed && (
              <div css={cardActionsContainerCss}>
                {showAssignmentAnchor && (
                  <AnchorWithTrackEvent
                    __cssFor={linkCss}
                    styleAnchorAsButton={{
                      flavor: Button.flavors.muted,
                      size: Button.sizes.lg,
                    }}
                    href={assignHref}
                    actionName={actionNames.navigate}
                    objectType={objectTypes.content}
                    streamProperties={{
                      content_type: contentType,
                      row_slug: dataQaSelector,
                      description:
                        "Navigate to create new assignment from bundle content card",
                    }}
                    __renderCustomLink={({
                      children,
                      href,
                      ...anchorProps
                    }) => {
                      return (
                        <Link
                          href={typeof href === "string" ? href : ""}
                          data-qa-selector={`${
                            dataQaSelector
                              ? `bundle-card-assign-link-${dataQaSelector}`
                              : "bundle-card-assign-link"
                          }`}
                          {...anchorProps}
                          aria-label="Create a new assignment from this content"
                        >
                          {children}
                        </Link>
                      );
                    }}
                  >
                    Assign
                  </AnchorWithTrackEvent>
                )}
              </div>
            )}
          </>
        }
      />
    );
  },
);
